import { type FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/Button'
import { ErrorBox } from '../components/ui/Card'
import { inputClass } from '../components/ui/FormField'

interface FieldErrors {
  full_name?: string
  email?: string
  password?: string
  confirm_password?: string
}

function validateEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
}

export default function Register() {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isLoading && user) void navigate('/dashboard', { replace: true })
  }, [user, isLoading, navigate])

  const validateAll = (): boolean => {
    const errors: FieldErrors = {}
    if (!fullName.trim()) errors.full_name = 'Full name is required'
    if (!email) errors.email = 'Email is required'
    else if (!validateEmail(email)) errors.email = 'Enter a valid email address'
    if (!password) errors.password = 'Password is required'
    else if (password.length < 8) errors.password = 'Password must be at least 8 characters'
    if (!confirmPassword) errors.confirm_password = 'Please confirm your password'
    else if (password !== confirmPassword) errors.confirm_password = 'Passwords do not match'
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleBlur = (field: keyof FieldErrors, value: string) => {
    const errors = { ...fieldErrors }
    if (field === 'full_name') {
      if (!value.trim()) errors.full_name = 'Full name is required'
      else delete errors.full_name
    }
    if (field === 'email') {
      if (!value) errors.email = 'Email is required'
      else if (!validateEmail(value)) errors.email = 'Enter a valid email address'
      else delete errors.email
    }
    if (field === 'password') {
      if (!value) errors.password = 'Password is required'
      else if (value.length < 8) errors.password = 'Password must be at least 8 characters'
      else delete errors.password
    }
    if (field === 'confirm_password') {
      if (!value) errors.confirm_password = 'Please confirm your password'
      else if (value !== password) errors.confirm_password = 'Passwords do not match'
      else delete errors.confirm_password
    }
    setFieldErrors(errors)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setApiError(null)
    if (!validateAll()) return
    setSubmitting(true)
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_name: fullName, email, password }),
      })
      if (res.status === 409) {
        setFieldErrors({ email: 'This email address is already registered' })
        return
      }
      if (res.status === 422) {
        const err = (await res.json()) as { detail?: string }
        setFieldErrors({ email: err.detail ?? 'Invalid email address' })
        return
      }
      if (!res.ok) {
        const err = (await res.json()) as { detail?: string }
        setApiError(err.detail ?? 'Registration failed. Please try again.')
        return
      }
      void navigate(`/verify-email?email=${encodeURIComponent(email)}`)
    } catch {
      setApiError('Network error. Please check your connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-card p-10 w-full max-w-[440px]">
        <div className="text-center font-bold text-[1.35rem] text-accent mb-1">Vet VPMS</div>
        <div className="text-center text-text-3 text-[0.9rem] mb-8">Create your account</div>

        {apiError && <ErrorBox>{apiError}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label className="block text-sm font-semibold text-text-heading mb-[0.35rem]" htmlFor="fullName">
            Full name
          </label>
          <input
            id="fullName"
            className={`${inputClass(!!fieldErrors.full_name)} mb-1`}
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            onBlur={(e) => handleBlur('full_name', e.target.value)}
            autoComplete="name"
            disabled={submitting}
          />
          {fieldErrors.full_name
            ? <span className="text-danger-text text-[0.8rem] mb-[0.85rem] block">{fieldErrors.full_name}</span>
            : <span className="mb-[0.85rem] block" />}

          <label className="block text-sm font-semibold text-text-heading mb-[0.35rem]" htmlFor="email">
            Email address
          </label>
          <input
            id="email"
            className={`${inputClass(!!fieldErrors.email)} mb-1`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={(e) => handleBlur('email', e.target.value)}
            autoComplete="email"
            disabled={submitting}
          />
          {fieldErrors.email
            ? <span className="text-danger-text text-[0.8rem] mb-[0.85rem] block">{fieldErrors.email}</span>
            : <span className="mb-[0.85rem] block" />}

          <label className="block text-sm font-semibold text-text-heading mb-[0.35rem]" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            className={`${inputClass(!!fieldErrors.password)} mb-1`}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={(e) => handleBlur('password', e.target.value)}
            autoComplete="new-password"
            disabled={submitting}
          />
          {fieldErrors.password
            ? <span className="text-danger-text text-[0.8rem] mb-[0.85rem] block">{fieldErrors.password}</span>
            : <span className="text-text-3 text-[0.8rem] mb-[0.85rem] block">
                {password.length > 0
                  ? password.length < 8
                    ? `${password.length}/8 characters minimum`
                    : <span className="inline-flex items-center gap-1"><Check size={14} strokeWidth={2.5} /> Password meets requirements</span>
                  : 'Minimum 8 characters'}
              </span>}

          <label className="block text-sm font-semibold text-text-heading mb-[0.35rem]" htmlFor="confirmPassword">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            className={`${inputClass(!!fieldErrors.confirm_password)} mb-1`}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onBlur={(e) => handleBlur('confirm_password', e.target.value)}
            autoComplete="new-password"
            disabled={submitting}
          />
          {fieldErrors.confirm_password
            ? <span className="text-danger-text text-[0.8rem] mb-[0.85rem] block">{fieldErrors.confirm_password}</span>
            : <span className="mb-[0.85rem] block" />}

          <Button type="submit" disabled={submitting} className="w-full mt-2">
            {submitting ? 'Creating account…' : 'Create Account'}
          </Button>
        </form>

        <div className="text-center mt-5 text-sm text-text-3">
          Already have an account?{' '}
          <Link to="/login" className="text-accent font-semibold no-underline hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
